import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";

const title = "Cursos escolares, de 3 a 18 años | SIS Estepona";
const description =
  "Preescolar, clase preescolar y primaria 1–6 en el centro, además de secundaria con Sofia Distans y bachillerato con Hermods, con tutorías en Estepona.";

export const Route = createFileRoute("/es/cursos-escolares")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/es/cursos-escolares") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks("/es/cursos-escolares"),
  }),
  component: CursosEscolares,
});

const levels = [
  {
    name: "Preescolar",
    age: "3–5 años",
    text: "Un comienzo lúdico con el sueco como lengua principal y una introducción temprana al español y al inglés.",
  },
  {
    name: "Clase preescolar",
    age: "5–6 años",
    text: "La transición a la escuela, centrada en el lenguaje, la convivencia y la seguridad dentro del grupo.",
  },
  {
    name: "Primaria",
    age: "Cursos 1–6",
    text: "Currículo sueco con profesorado titulado, valoraciones escritas y español ampliado en el horario.",
  },
  {
    name: "Secundaria",
    age: "Cursos 7–9",
    text: "Estudios a través de Sofia Distans con tutores presentes en las instalaciones del colegio.",
  },
  {
    name: "Bachillerato",
    age: "Cursos 1–3",
    text: "Estudios a través de Hermods con nuestra tutorización, combinables con un perfil deportivo.",
  },
  {
    name: "Extraescolares",
    age: "Después de clase",
    text: "Actividades de tarde junto al colegio, con movimiento y tiempo al aire libre.",
  },
];

function CursosEscolares() {
  return (
    <>
      <PageHero
        eyebrow="Cursos escolares"
        title="Desde preescolar hasta bachillerato"
        intro="Swedish International School acoge en el centro a alumnado desde preescolar hasta 6.º curso, y a alumnado de secundaria y bachillerato mediante educación a distancia con tutorización. La enseñanza es en sueco, con español e inglés ampliados."
      />

      <section className="mx-auto max-w-6xl px-5 py-16">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {levels.map((l) => (
            <article key={l.name} className="rounded-xl border border-border bg-card p-7 shadow-soft">
              <p className="text-xs font-semibold tracking-[0.18em] text-primary uppercase">{l.age}</p>
              <h2 className="mt-3 text-xl font-semibold">{l.name}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{l.text}</p>
            </article>
          ))}
        </div>
      </section>

      <Prose>
        <h2>Cómo encajan las etapas</h2>
        <p>
          El colegio sigue el currículo nacional sueco y trabaja en grupos por edades. El preescolar
          se centra en el juego, el lenguaje y la seguridad; la clase preescolar prepara para el
          trabajo escolar; y a partir del curso 1 la enseñanza sigue los contenidos básicos de cada
          asignatura. Al ser un colegio pequeño, el profesorado trabaja en estrecha colaboración y
          conoce a cada alumno, lo que facilita adaptar el ritmo y los contenidos.
        </p>
        <p>
          Desde secundaria, el alumnado estudia con Sofia Distans y, en bachillerato, con Hermods.
          Los alumnos están en el colegio durante la jornada y cuentan con tutores que les ayudan con
          la planificación, la técnica de estudio y el seguimiento. Las notas y los certificados los
          emite la escuela a distancia correspondiente.
        </p>

        <h2>Los idiomas a lo largo de la escolaridad</h2>
        <p>
          La lengua de enseñanza es el sueco. El español está en el horario desde preescolar y
          continúa durante toda la primaria, y el inglés se introduce pronto y se amplía conforme al
          currículo. Muchos de nuestros alumnos viven en un día a día multilingüe, y el objetivo es
          que cambien de idioma con confianza: en el aula, en el patio y fuera del colegio.
        </p>

        <h2>Transiciones entre etapas</h2>
        <p>
          Las transiciones se planifican junto con las familias. Antes de que un niño empiece con
          nosotros revisamos su escolarización previa, posibles necesidades de apoyo y su punto en el
          currículo, para que el comienzo sea lo más suave posible. Es posible empezar a mitad de
          curso; escribidnos y comprobamos la disponibilidad en el grupo correspondiente.
        </p>
        <p>
          Si tenéis dudas sobre qué curso corresponde, partimos del año de nacimiento según el modelo
          sueco, pero siempre hacemos una valoración individual con la familia. El{" "}
          <a className="text-primary link-quiet" href="/es/solicitud">formulario de solicitud</a> propone un curso
          automáticamente según la fecha de nacimiento, y podéis cambiarlo.
        </p>

        <h2>Qué incluye la jornada escolar</h2>
        <p>
          La jornada incluye la comida en el colegio, tiempo al aire libre y movimiento. Después de
          clase hay{" "}
          <a className="text-primary link-quiet" href="/es/actividades-extraescolares">actividades extraescolares</a>
          , y perfiles deportivos opcionales de pádel y tenis además de la academia de fútbol. Las
          tarifas y lo que incluye cada etapa están en la{" "}
          <a className="text-primary link-quiet" href="/es/tarifas">página de tarifas</a>.
        </p>
      </Prose>

      <FaqBlock items={faqFor("arskurser", "es")} locale="es" />

      <CtaBand
        title="¿No sabéis qué curso corresponde?"
        text="Escribidnos y repasamos juntos las opciones para vuestra familia."
        locale="es"
      />
      <div className="h-20" />
    </>
  );
}
